import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Auth from "../../auth/auth";
import fingerPrint from "../../helpers/fingerprintjs";
import { AuthService } from "../../Services/authServices";
import ForgotPasswordModal from "../../components/Modal/ForgotPassword.modal";
import { defaultTheme, BrandPanel, NxField, NxButton, NxIcon, PageShell } from "../../components/nx";
import ReCAPTCHA from "react-google-recaptcha";

export default function Login() {
  const navigate = useNavigate();
  const t = defaultTheme;

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHA>(null);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken(null);
  };

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const captchaEnabled = Boolean(process.env.REACT_APP_SITE_KEY);

  const loginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (captchaEnabled && !captchaToken) {
      toast.error("Please complete the captcha verification");
      return;
    }

    setLoading(true);
    try {
      const res = await AuthService.login({
        email: loginData.email,
        password: loginData.password,
        ...(captchaToken ? { captchaToken: captchaToken } : {}),
        devInfo: await fingerPrint(),
      });
      if (res.status === 200) {
        await Auth.authenticate();
        localStorage.setItem("authKey", res.data.data.token);
        toast.success("Login Success");
        navigate("/exam/student-info");
      }
    } catch (err: any) {
      // reCAPTCHA tokens are single-use — reset so the user can retry
      resetCaptcha();
      if (err.response?.status === 403) {
        toast.error("Something went wrong");
      } else if (err.response?.status === 500) {
        toast.error(err.response.data);
      } else {
        toast.error("Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageShell t={t} style={{ display: "flex", height: "100vh" }}>
        {/* Brand panel — left */}
        <div style={{ width: "46%", height: "100%" }}>
          <BrandPanel t={t} />
        </div>

        {/* Form — right */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
          <form onSubmit={loginSubmit} style={{ width: "100%", maxWidth: 380 }}>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{
                // fontFamily: '"Instrument Serif", serif',
                fontSize: 38, fontWeight: 400, lineHeight: 1.05,
                color: t.ink, letterSpacing: "-0.01em", margin: 0,
              }}>Welcome back.</h1>
              <div style={{ fontSize: 14, color: t.inkSoft, marginTop: 8 }}>
                Sign in to your candidate workspace.
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <NxField t={t} label="Username" name="email" value={loginData.email} placeholder="940DIHAO" onChange={onChangeValue} />
              <NxField
                t={t}
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={loginData.password}
                placeholder="Enter your password"
                onChange={onChangeValue}
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 32, height: 32, borderRadius: 6,
                      background: "transparent", border: "none", cursor: "pointer",
                      color: t.inkMute,
                    }}
                  >
                    {showPassword ? NxIcon.eyeOff(t.inkSoft) : NxIcon.eye(t.inkSoft)}
                  </button>
                }
              />

              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: 2 }}>
                <a
                  style={{ fontSize: 13, color: t.accent, textDecoration: "none", cursor: "pointer" }}
                  onClick={() => setShowForgotModal(true)}
                >
                  Forgot password?
                </a>
              </div>

              {captchaEnabled && (
                <div style={{ marginTop: 2 }}>
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={process.env.REACT_APP_SITE_KEY as string}
                    onChange={handleCaptchaChange}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              <NxButton t={t} kind="primary" w="100%" icon={NxIcon.arrowRight("#fff")} type="submit" disabled={loading || (captchaEnabled && !captchaToken)}>
                {loading ? "Signing in..." : "Sign in"}
              </NxButton>

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6, marginBottom: 2 }}>
                <div style={{ flex: 1, height: 1, background: t.border }} />
                <span style={{ fontSize: 11, color: t.inkMute, letterSpacing: "0.06em", textTransform: "uppercase" }}>or</span>
                <div style={{ flex: 1, height: 1, background: t.border }} />
              </div>

              <div style={{ textAlign: "center", fontSize: 13, color: t.inkSoft, marginTop: 8 }}>
                New to nixassign?{" "}
                <a
                  style={{
                    color: t.ink, fontWeight: 500, textDecoration: "none",
                    borderBottom: `1px solid ${t.ink}`, paddingBottom: 1, cursor: "pointer",
                  }}
                  onClick={() => navigate("/signup")}
                >
                  Create an account
                </a>
              </div>
            </div>
          </form>
        </div>
      </PageShell>

      <ForgotPasswordModal
        show={showForgotModal}
        handleClose={() => setShowForgotModal(false)}
      />
    </>
  );
}
